import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../config";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faSpinner, faCog } from "@fortawesome/free-solid-svg-icons";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";

const CorporateBillingSetup = () => {
  const { corp_code } = useParams();
  const navigate = useNavigate();
  
  const [corporateInfo, setCorporateInfo] = useState({
    corporateCode: "",
    corporateName: "",
  });
  
  const [data, setData] = useState({
    network_access_fee: 0.0,
    admin_fee: 0.0,
    card_printing_fee: 0.0,
    card_replacement_fee: 0.0,
    interest_rate: 0.0,
    payment_terms: 30,
    split_naf_accounts: false,
    split_naf_principal_dependent: false,
    split_naf_na: false,
    split_af_accounts: false,
    split_af_ip_op: false,
    split_af_na: false,
    split_cpf_accounts: false,
    split_cpf_principal_dependent: false,
    split_cpf_na: false,
    split_crf_accounts: false,
    split_crf_principal_dependent: false,
    split_crf_na: false,
    is_vatable: false,
    principal_vat_status: "Non-Vatable",
    dependent_vat_status: "Non-Vatable",
    billing_type: "SI",
  });
  
  const [applyToAll, setApplyToAll] = useState(false);
  const [applyNetworkAccessFee, setApplyNetworkAccessFee] = useState(true);
  const [applyAdminFee, setApplyAdminFee] = useState(true);
  const [applyCardPrintingFee, setApplyCardPrintingFee] = useState(true);
  const [applyCardReplacementFee, setApplyCardReplacementFee] = useState(true);
  const [applyInterestRate, setApplyInterestRate] = useState(true);
  const [applyPaymentTerms, setApplyPaymentTerms] = useState(true);
  const [applySplitNafAccounts, setApplySplitNafAccounts] = useState(true);
  const [applySplitNafPrincipalDependent, setApplySplitNafPrincipalDependent] = useState(true);
  const [applySplitNafNa, setApplySplitNafNa] = useState(true);
  const [applySplitAfAccounts, setApplySplitAfAccounts] = useState(true);
  const [applySplitAfIpOp, setApplySplitAfIpOp] = useState(true);
  const [applySplitAfNa, setApplySplitAfNa] = useState(true);
  const [applySplitCpfAccounts, setApplySplitCpfAccounts] = useState(true);
  const [applySplitCpfPrincipalDependent, setApplySplitCpfPrincipalDependent] = useState(true);
  const [applySplitCpfNa, setApplySplitCpfNa] = useState(true);
  const [applySplitCrfAccounts, setApplySplitCrfAccounts] = useState(true);
  const [applySplitCrfPrincipalDependent, setApplySplitCrfPrincipalDependent] = useState(true);
  const [applySplitCrfNa, setApplySplitCrfNa] = useState(true);
  const [applyIsVatable, setApplyIsVatable] = useState(true);
  const [applyPrincipalVatStatus, setApplyPrincipalVatStatus] = useState(true);
  const [applyDependentVatStatus, setApplyDependentVatStatus] = useState(true);
  const [applyBillingType, setApplyBillingType] = useState(true);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    network_access_fee: "",
    admin_fee: "",
    card_printing_fee: "",
    card_replacement_fee: "",
    interest_rate: "",
    payment_terms: "",
  });

  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(AdminAccessAlertContext);

  useEffect(() => {
    fetchData();
  }, [corp_code]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const corporateResponse = await axios.get(`${BASE_URL}/corporate/${corp_code}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      const corporate = corporateResponse.data.data;
      setCorporateInfo({
        corporateCode: corporate.corporateCode,
        corporateName: corporate.corporateName,
      });

      try {
        const billingResponse = await axios.get(`${BASE_URL}/billing-configuration/${corp_code}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        if (billingResponse.data.data) {
          setData(billingResponse.data.data);
        }
      } catch (billingError) {
        if (billingError.response?.status !== 404) {
          throw billingError;
        }
      }
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
      if (error.response?.status === 404) {
        Swal.fire({
          title: "Not Found!",
          html: `<div class="text-center text-sm">
                   <p>The corporate record you are looking for does not exist.</p>
                   <p>You will be redirected to the corporate settings page.</p>
                 </div>`,
          icon: "error",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/corporate-settings");
        });
      } else {
        Swal.fire({
          title: "Error!",
          html: `<div class="text-center text-sm">
                   <p>Failed to fetch corporate or billing details.</p>
                   <p>Please try again later or contact support.</p>
                 </div>`,
          icon: "error",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const validateFee = (field, value) => {
    if (value < 0) {
      setErrors((prev) => ({ ...prev, [field]: `${field.replace('_', ' ').replace('fee', 'Fee')} cannot be negative.` }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return true;
    }
  };

  const validateInterestRate = (value) => {
    if (value < 0 || value > 100) {
      setErrors((prev) => ({ ...prev, interest_rate: "Interest Rate must be between 0 and 100%." }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, interest_rate: "" }));
      return true;
    }
  };

  const validatePaymentTerms = (value) => {
    if (value <= 0) {
      setErrors((prev) => ({ ...prev, payment_terms: "Payment Terms must be greater than 0 days." }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, payment_terms: "" }));
      return true;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setData((prev) => {
        const newData = { ...prev, [name]: checked };
        // Handle NAF split logic
        if (name === "split_naf_accounts" || name === "split_naf_principal_dependent") {
          if (checked) {
            newData.split_naf_na = false;
          }
        } else if (name === "split_naf_na" && checked) {
          newData.split_naf_accounts = false;
          newData.split_naf_principal_dependent = false;
        }
        // Handle AF split logic
        if (name === "split_af_accounts" || name === "split_af_ip_op") {
          if (checked) {
            newData.split_af_na = false;
          }
        } else if (name === "split_af_na" && checked) {
          newData.split_af_accounts = false;
          newData.split_af_ip_op = false;
        }
        // Handle CPF split logic
        if (name === "split_cpf_accounts" || name === "split_cpf_principal_dependent") {
          if (checked) {
            newData.split_cpf_na = false;
          }
        } else if (name === "split_cpf_na" && checked) {
          newData.split_cpf_accounts = false;
          newData.split_cpf_principal_dependent = false;
        }
        // Handle CRF split logic
        if (name === "split_crf_accounts" || name === "split_crf_principal_dependent") {
          if (checked) {
            newData.split_crf_na = false;
          }
        } else if (name === "split_crf_na" && checked) {
          newData.split_crf_accounts = false;
          newData.split_crf_principal_dependent = false;
        }
        return newData;
      });
    } else if (type === "number") {
      const numericValue = parseFloat(value) || 0;
      setData((prev) => ({ ...prev, [name]: numericValue }));
      if (name === "interest_rate") {
        validateInterestRate(numericValue);
      } else if (name === "payment_terms") {
        validatePaymentTerms(numericValue);
      } else {
        validateFee(name, numericValue);
      }
    } else {
      setData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (field) => {
    if (field === "interest_rate") {
      validateInterestRate(data.interest_rate);
    } else if (field === "payment_terms") {
      validatePaymentTerms(data.payment_terms);
    } else {
      validateFee(field, data[field]);
    }
  };

  const handleApplyToAllChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      Swal.fire({
        title: "Are you sure?",
        html: `<div class="text-center text-sm">
                 <p>Do you want to apply the selected changes to all accounts of</p>
                 <p><strong>${corporateInfo.corporateName}</strong>?</p>
                 <p>You can choose which fields to update in the submenu.</p>
               </div>`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#ef4444",
        confirmButtonText: "Yes, apply to all!",
        cancelButtonText: "No, cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          setApplyToAll(true);
        } else {
          e.target.checked = false;
        }
      });
    } else {
      Swal.fire({
        title: "Notice",
        html: `<div class="text-center text-sm">
                 <p>The changes you made will only apply to this corporate:</p>
                 <p><strong>${corporateInfo.corporateName}</strong>.</p>
               </div>`,
        icon: "info",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      }).then(() => {
        setApplyToAll(false);
        setApplyNetworkAccessFee(true);
        setApplyAdminFee(true);
        setApplyCardPrintingFee(true);
        setApplyCardReplacementFee(true);
        setApplyInterestRate(true);
        setApplyPaymentTerms(true);
        setApplySplitNafAccounts(true);
        setApplySplitNafPrincipalDependent(true);
        setApplySplitNafNa(true);
        setApplySplitAfAccounts(true);
        setApplySplitAfIpOp(true);
        setApplySplitAfNa(true);
        setApplySplitCpfAccounts(true);
        setApplySplitCpfPrincipalDependent(true);
        setApplySplitCpfNa(true);
        setApplySplitCrfAccounts(true);
        setApplySplitCrfPrincipalDependent(true);
        setApplySplitCrfNa(true);
        setApplyIsVatable(true);
        setApplyPrincipalVatStatus(true);
        setApplyDependentVatStatus(true);
        setApplyBillingType(true);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validations = [
      validateFee("network_access_fee", data.network_access_fee),
      validateFee("admin_fee", data.admin_fee),
      validateFee("card_printing_fee", data.card_printing_fee),
      validateFee("card_replacement_fee", data.card_replacement_fee),
      validateInterestRate(data.interest_rate),
      validatePaymentTerms(data.payment_terms),
    ];

    if (!validations.every((valid) => valid)) {
      Swal.fire({
        title: "Error!",
        html: `<div class="text-center text-sm">
                 <p>Please correct the errors in the form before submitting.</p>
               </div>`,
        icon: "error",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    let noticeMessage = "";
    if (applyToAll) {
      const fieldsToUpdate = [];
      if (applyNetworkAccessFee) fieldsToUpdate.push("Network Access Fee");
      if (applyAdminFee) fieldsToUpdate.push("Admin Fee");
      if (applyCardPrintingFee) fieldsToUpdate.push("Card Printing Fee");
      if (applyCardReplacementFee) fieldsToUpdate.push("Card Replacement Fee");
      if (applyInterestRate) fieldsToUpdate.push("Interest Rate");
      if (applyPaymentTerms) fieldsToUpdate.push("Payment Terms");
      if (applySplitNafAccounts) fieldsToUpdate.push("Split NAF Accounts");
      if (applySplitNafPrincipalDependent) fieldsToUpdate.push("Split NAF Principal/Dependent");
      if (applySplitNafNa) fieldsToUpdate.push("Split NAF NA");
      if (applySplitAfAccounts) fieldsToUpdate.push("Split AF Accounts");
      if (applySplitAfIpOp) fieldsToUpdate.push("Split AF IP/OP");
      if (applySplitAfNa) fieldsToUpdate.push("Split AF NA");
      if (applySplitCpfAccounts) fieldsToUpdate.push("Split CPF Accounts");
      if (applySplitCpfPrincipalDependent) fieldsToUpdate.push("Split CPF Principal/Dependent");
      if (applySplitCpfNa) fieldsToUpdate.push("Split CPF NA");
      if (applySplitCrfAccounts) fieldsToUpdate.push("Split CRF Accounts");
      if (applySplitCrfPrincipalDependent) fieldsToUpdate.push("Split CRF Principal/Dependent");
      if (applySplitCrfNa) fieldsToUpdate.push("Split CRF NA");
      if (applyIsVatable) fieldsToUpdate.push("Is VATable");
      if (applyPrincipalVatStatus) fieldsToUpdate.push("Principal VAT Status");
      if (applyDependentVatStatus) fieldsToUpdate.push("Dependent VAT Status");
      if (applyBillingType) fieldsToUpdate.push("Billing Type");
      
      const fieldsText = fieldsToUpdate.length > 0 
        ? `The following field(s) will be updated for all accounts:<br/><ul class="text-left mx-auto">${fieldsToUpdate.map(field => `<li>- ${field}</li>`).join('')}</ul>`
        : `No fields are selected to update for all accounts. Only the current record will be updated.`;
      noticeMessage = fieldsText;
    } else {
      noticeMessage = `The changes will only apply to this corporate: <strong>${corporateInfo.corporateName}</strong>.`;
    }

    Swal.fire({
      title: "Notice",
      html: `<div class="text-center text-sm">${noticeMessage}</div>`,
      icon: "info",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "Proceed",
    }).then((noticeResult) => {
      if (noticeResult.isConfirmed) {
        Swal.fire({
          title: "Are you sure?",
          html: `<div class="text-center text-sm">
                   <p>Do you want to save the billing configuration for <strong>${corporateInfo.corporateName}</strong>?</p>
                 </div>`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#2563eb",
          cancelButtonColor: "#ef4444",
          confirmButtonText: "Yes, save it!",
          cancelButtonText: "No, cancel",
        }).then(async (confirmResult) => {
          if (confirmResult.isConfirmed) {
            setSubmitting(true);
            const startTime = Date.now();

            try {
              await axios.post(
                `${BASE_URL}/billing-configuration/${corp_code}`,
                {
                  ...data,
                  applyToAll,
                  applyNetworkAccessFee,
                  applyAdminFee,
                  applyCardPrintingFee,
                  applyCardReplacementFee,
                  applyInterestRate,
                  applyPaymentTerms,
                  applySplitNafAccounts,
                  applySplitNafPrincipalDependent,
                  applySplitNafNa,
                  applySplitAfAccounts,
                  applySplitAfIpOp,
                  applySplitAfNa,
                  applySplitCpfAccounts,
                  applySplitCpfPrincipalDependent,
                  applySplitCpfNa,
                  applySplitCrfAccounts,
                  applySplitCrfPrincipalDependent,
                  applySplitCrfNa,
                  applyIsVatable,
                  applyPrincipalVatStatus,
                  applyDependentVatStatus,
                  applyBillingType,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
                  },
                }
              );
              Swal.fire({
                title: "Updated!",
                html: `<div class="text-center text-sm">
                         <p>Billing configuration has been successfully updated.</p>
                       </div>`,
                icon: "success",
                confirmButtonColor: "#2563eb",
                confirmButtonText: "OK",
              });
            } catch (error) {
              if (error.response?.status === 401) window.location.href = "/login";
              if (error.response?.status === 403) openAdminAccessAlert();
              if (error.response?.status === 429) openRateLimitAlert();
              Swal.fire({
                title: "Error!",
                html: `<div class="text-center text-sm">
                         <p>Failed to update billing configuration.</p>
                         <p>Please try again or contact support.</p>
                       </div>`,
                icon: "error",
                confirmButtonColor: "#2563eb",
                confirmButtonText: "OK",
              });
            } finally {
              const elapsedTime = Date.now() - startTime;
              const minLoadingTime = 1000;
              if (elapsedTime < minLoadingTime) {
                await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
              }
              setSubmitting(false);
            }
          }
        });
      }
    });
  };

  const handleBack = () => {
    navigate(`/corporate-settings`);
  };
  const handleDetails = () => {
    navigate(`/corporate-settings/details/${corp_code}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={handleBack}
            className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Setup Billing Fees</h1>
            <p className="text-xs text-gray-500">Setup for {corporateInfo.corporateName || "Corporate"}</p>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600 text-xl" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Corporate Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Corporate Code</label>
                  <input
                    type="text"
                    value={corporateInfo.corporateCode}
                    disabled
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Corporate Name</label>
                  <input
                    type="text"
                    value={corporateInfo.corporateName}
                    disabled
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              {/* Fee Configuration */}
              <div>
                <h2 className="text-sm font-semibold text-gray-800 mb-2">Fee Rates</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { name: "network_access_fee", label: "Network Access Fee" },
                    { name: "admin_fee", label: "Admin Fee (%)" },
                    { name: "card_printing_fee", label: "Card Printing Fee" },
                    { name: "card_replacement_fee", label: "Card Replacement Fee" },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-xs font-medium text-gray-700">{field.label}</label>
                      <input
                        type="number"
                        name={field.name}
                        value={data[field.name]}
                        onChange={handleChange}
                        onBlur={() => handleBlur(field.name)}
                        step="0.01"
                        min="0"
                        className={`mt-1 w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          errors[field.name] ? "border-red-500" : "border-gray-200"
                        }`}
                      />
                      {errors[field.name] && (
                        <p className="text-xs text-red-500 mt-1">{errors[field.name]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Interest and Payment Terms */}
              <div>
                <h2 className="text-sm font-semibold text-gray-800 mb-2">Interest & Payment Terms</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Interest Rate (%)</label>
                    <input
                      type="number"
                      name="interest_rate"
                      value={data.interest_rate}
                      onChange={handleChange}
                      onBlur={() => handleBlur("interest_rate")}
                      step="0.01"
                      min="0"
                      max="100"
                      className={`mt-1 w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.interest_rate ? "border-red-500" : "border-gray-200"
                      }`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Applied after 5-day grace period</p>
                    {errors.interest_rate && (
                      <p className="text-xs text-red-500 mt-1">{errors.interest_rate}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Payment Terms (Days)</label>
                    <input
                      type="number"
                      name="payment_terms"
                      value={data.payment_terms}
                      onChange={handleChange}
                      onBlur={() => handleBlur("payment_terms")}
                      min="1"
                      className={`mt-1 w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                        errors.payment_terms ? "border-red-500" : "border-gray-200"
                      }`}
                    />
                    {errors.payment_terms && (
                      <p className="text-xs text-red-500 mt-1">{errors.payment_terms}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Split Billing Configuration */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="text-sm font-semibold text-gray-800 mb-2">Split Billing Configuration</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    {
                      title: "Network Access Fee (NAF)",
                      items: [
                        { id: "split_naf_accounts", label: "Split by Accounts", disabled: data.split_naf_na },
                        { id: "split_naf_principal_dependent", label: "Split by Principal/Dependent", disabled: data.split_naf_na },
                        { id: "split_naf_na", label: "Normal Billing (Main Corp)", disabled: data.split_naf_accounts || data.split_naf_principal_dependent },
                      ],
                    },
                    {
                      title: "Admin Fee (AF)",
                      items: [
                        { id: "split_af_accounts", label: "Split by Accounts", disabled: data.split_af_na },
                        { id: "split_af_ip_op", label: "Split by Inpatient/Outpatient", disabled: data.split_af_na },
                        { id: "split_af_na", label: "Normal Billing (Main Corp)", disabled: data.split_af_accounts || data.split_af_ip_op },
                      ],
                    },
                    {
                      title: "Card Printing Fee (CPF)",
                      items: [
                        { id: "split_cpf_accounts", label: "Split by Accounts", disabled: data.split_cpf_na },
                        { id: "split_cpf_principal_dependent", label: "Split by Principal/Dependent", disabled: data.split_cpf_na },
                        { id: "split_cpf_na", label: "Normal Billing (Main Corp)", disabled: data.split_cpf_accounts || data.split_cpf_principal_dependent },
                      ],
                    },
                    {
                      title: "Card Replacement Fee (CRF)",
                      items: [
                        { id: "split_crf_accounts", label: "Split by Accounts", disabled: data.split_crf_na },
                        { id: "split_crf_principal_dependent", label: "Split by Principal/Dependent", disabled: data.split_crf_na },
                        { id: "split_crf_na", label: "Normal Billing (Main Corp)", disabled: data.split_crf_accounts || data.split_crf_principal_dependent },
                      ],
                    },
                  ].map((section) => (
                    <div key={section.title} className="bg-white p-3 rounded-md shadow-sm">
                      <h3 className="text-xs font-semibold text-gray-700 mb-2 bg-blue-50 px-2 py-1 rounded">{section.title}</h3>
                      <div className="space-y-2">
                        {section.items.map((item) => (
                          <div key={item.id} className="flex items-center">
                            <input
                              type="checkbox"
                              id={item.id}
                              name={item.id}
                              checked={data[item.id]}
                              onChange={handleChange}
                              disabled={item.disabled}
                              className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500 disabled:opacity-50"
                            />
                            <label htmlFor={item.id} className="ml-2 text-xs text-gray-700">{item.label}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* VAT and Billing Type */}
              <div>
                <h2 className="text-sm font-semibold text-gray-800 mb-2">VAT & Billing Type</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="is_vatable"
                        name="is_vatable"
                        checked={data.is_vatable}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_vatable" className="ml-2 text-xs text-gray-700">VATable (Apply VAT to Fees)</label>
                    </div>
                    {data.is_vatable && (
                      <div className="space-y-2 ml-6">
                        <div>
                          <label className="block text-xs font-medium text-gray-700">Principal VAT Status</label>
                          <select
                            name="principal_vat_status"
                            value={data.principal_vat_status}
                            onChange={handleChange}
                            className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="Vatable">Vatable</option>
                            <option value="Non-Vatable">Non-Vatable</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700">Dependent VAT Status</label>
                          <select
                            name="dependent_vat_status"
                            value={data.dependent_vat_status}
                            onChange={handleChange}
                            className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="Vatable">Vatable</option>
                            <option value="Non-Vatable">Non-Vatable</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Default Billing Type</label>
                    <select
                      name="billing_type"
                      value={data.billing_type}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="SI">Sales Invoice (SI)</option>
                      <option value="SOA">Statement of Account (SOA)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Apply to All */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="applyToAll"
                    checked={applyToAll}
                    onChange={handleApplyToAllChange}
                    className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="applyToAll" className="ml-2 text-xs text-gray-700">Apply to all Accounts</label>
                </div>
                {applyToAll && (
                  <div className="mt-2 ml-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { id: "applyNetworkAccessFee", label: "Network Access Fee", state: applyNetworkAccessFee, setter: setApplyNetworkAccessFee },
                      { id: "applyAdminFee", label: "Admin Fee", state: applyAdminFee, setter: setApplyAdminFee },
                      { id: "applyCardPrintingFee", label: "Card Printing Fee", state: applyCardPrintingFee, setter: setApplyCardPrintingFee },
                      { id: "applyCardReplacementFee", label: "Card Replacement Fee", state: applyCardReplacementFee, setter: setApplyCardReplacementFee },
                      { id: "applyInterestRate", label: "Interest Rate", state: applyInterestRate, setter: setApplyInterestRate },
                      { id: "applyPaymentTerms", label: "Payment Terms", state: applyPaymentTerms, setter: setApplyPaymentTerms },
                      { id: "applySplitNafAccounts", label: "Split NAF Accounts", state: applySplitNafAccounts, setter: setApplySplitNafAccounts },
                      { id: "applySplitNafPrincipalDependent", label: "Split NAF Principal/Dependent", state: applySplitNafPrincipalDependent, setter: setApplySplitNafPrincipalDependent },
                      { id: "applySplitNafNa", label: "Split NAF NA", state: applySplitNafNa, setter: setApplySplitNafNa },
                      { id: "applySplitAfAccounts", label: "Split AF Accounts", state: applySplitAfAccounts, setter: setApplySplitAfAccounts },
                      { id: "applySplitAfIpOp", label: "Split AF IP/OP", state: applySplitAfIpOp, setter: setApplySplitAfIpOp },
                      { id: "applySplitAfNa", label: "Split AF NA", state: applySplitAfNa, setter: setApplySplitAfNa },
                      { id: "applySplitCpfAccounts", label: "Split CPF Accounts", state: applySplitCpfAccounts, setter: setApplySplitCpfAccounts },
                      { id: "applySplitCpfPrincipalDependent", label: "Split CPF Principal/Dependent", state: applySplitCpfPrincipalDependent, setter: setApplySplitCpfPrincipalDependent },
                      { id: "applySplitCpfNa", label: "Split CPF NA", state: applySplitCpfNa, setter: setApplySplitCpfNa },
                      { id: "applySplitCrfAccounts", label: "Split CRF Accounts", state: applySplitCrfAccounts, setter: setApplySplitCrfAccounts },
                      { id: "applySplitCrfPrincipalDependent", label: "Split CRF Principal/Dependent", state: applySplitCrfPrincipalDependent, setter: setApplySplitCrfPrincipalDependent },
                      { id: "applySplitCrfNa", label: "Split CRF NA", state: applySplitCrfNa, setter: setApplySplitCrfNa },
                      { id: "applyIsVatable", label: "Is VATable", state: applyIsVatable, setter: setApplyIsVatable },
                      { id: "applyPrincipalVatStatus", label: "Principal VAT Status", state: applyPrincipalVatStatus, setter: setApplyPrincipalVatStatus },
                      { id: "applyDependentVatStatus", label: "Dependent VAT Status", state: applyDependentVatStatus, setter: setApplyDependentVatStatus },
                      { id: "applyBillingType", label: "Billing Type", state: applyBillingType, setter: setApplyBillingType },
                    ].map((item) => (
                      <div key={item.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={item.id}
                          checked={item.state}
                          onChange={(e) => item.setter(e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-200 rounded focus:ring-blue-500"
                        />
                        <label htmlFor={item.id} className="ml-2 text-xs text-gray-700">{item.label}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-3 py-2 text-xs text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-1" /> Back
                </button>
                <button
                  type="button"
                  onClick={handleDetails}
                  className="px-3 py-2 text-xs text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faCog} className="mr-1" /> Corporate Details
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-2 text-xs text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-1" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateBillingSetup;